import React from "react";
import { Icons } from "../../components/common/Icons";
import { useInternData } from "../../context/InternContext";
import EmployeeDocumentDashboard from "../../components/common/EmployeeDocumentDashboard";
import "../../assets/styles/Intern/InternWidgets.css";

function TimelineProgress() {
  const { internData } = useInternData();
  const now = new Date();
  const start = internData.startDate ? new Date(internData.startDate) : null;
  const end = internData.endDate ? new Date(internData.endDate) : null;
  const onboardingProgress = internData.progress || 0;
  let periodProgress = 0;
  if (start && end && end > start) {
    const total = end.getTime() - start.getTime();
    const elapsed = Math.max(0, now.getTime() - start.getTime());
    periodProgress = Math.min(100, Math.round((elapsed / total) * 100));
  }
  const progress = Math.round(onboardingProgress * 0.4 + periodProgress * 0.6);
  const milestones = [
    { label: "Onboarding", status: progress >= 10 ? "completed" : progress > 0 ? "current" : "pending" },
    { label: "In Progress", status: progress >= 40 ? "completed" : progress >= 10 ? "current" : "pending" },
    { label: "Near Completion", status: progress >= 80 ? "completed" : progress >= 40 ? "current" : "pending" },
    { label: "Completed", status: progress >= 100 ? "completed" : progress >= 80 ? "current" : "pending" }
  ];
  const dateStr = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");

  return (
    <div className="card animate-in delay-2 internwidgets-inline-1">
      <div className="card-header">
        <div>
          <div className="card-title">Internship Timeline</div>
          <div className="card-subtitle">{dateStr(internData.startDate)} to {dateStr(internData.endDate)}</div>
        </div>
        <div className="internwidgets-inline-2">
          <div className="progress-pct">{progress}%</div>
          <div className="progress-pct-sub">completed</div>
        </div>
      </div>
      <div className="card-body">
        <div className="timeline-bar-track">
          <div className="timeline-bar-fill internwidgets-dyn-1" style={{ "--internwidgets-1-width": `${progress}%` }} />
        </div>
        <div className="timeline-milestones">
          {milestones.map((m, i) => (
            <div className="timeline-milestone" key={i}>
              <div className={`milestone-dot ${m.status}`} />
              <div className={`milestone-label ${m.status === "current" ? "active" : ""}`}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Checklist() {
  const { checklist } = useInternData();
  return (
    <div className="card animate-in delay-3">
      <div className="card-header">
        <div className="card-title">Internship Checklist</div>
      </div>
      <div className="card-body">
        {(checklist || []).map((item, i) => (
          <div className="checklist-item" key={i}>
            <div className={`checklist-check ${item.done ? "done" : "pending"}`}>{item.done && <Icons.Check />}</div>
            <div className={`checklist-label ${item.done ? "" : "pending"}`}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OnboardingDocs() {
  const { onboardingDocs, internData, refreshInternData } = useInternData();
  const handleDownload = async (doc) => {
    try {
      if (!doc.id) return alert("Document download not available");
      const api = (await import("../../services/api")).default;
      await api.downloadDocById(doc.id, doc.name || doc.doc_name || "document");
    } catch (e) {
      alert("Download failed: " + e.message);
    }
  };

  return (
    <>
      <EmployeeDocumentDashboard docs={onboardingDocs || []} onRefresh={refreshInternData} />
      <div className="card animate-in delay-3">
        <div className="card-header">
          <div>
            <div className="card-title">Onboarding and Assignments</div>
            <div className="card-subtitle">Official documents and details</div>
          </div>
        </div>
        <div className="card-body">
          {(onboardingDocs || []).map((doc, i) => (
            <div className="doc-item" key={i}>
              <div className="doc-name">Document {doc.name || doc.doc_name || "Document"}</div>
              <div className="doc-download internwidgets-inline-3" onClick={() => handleDownload(doc)}>
                <Icons.Download /> Download
              </div>
            </div>
          ))}
          <div className="internwidgets-inline-4">
            <div className="internwidgets-inline-5">Assignment Details</div>
            {[["Team", internData.team], ["Role", internData.role], ["Email", internData.email || "-"], ["Start", internData.startDate || "-"]].map(([l, v], i) => (
              <div className="assign-row" key={i}>
                <div className="assign-label">{l}</div>
                <div className="assign-value">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function InternModal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <div className="modal-close" onClick={onClose}><Icons.Close /></div>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const labels = { manager_approved: "PM Approved" };
  return <span className={`status-badge ${status}`}><span className="sb-dot" />{labels[status] || status}</span>;
}

export { TimelineProgress, Checklist, OnboardingDocs, InternModal, StatusBadge };
